/**
 * `attach-repository` wizard step renderer (design §7) — repository radio
 * cards over `GET /api/repositories` (page 1, limit 100; no client search is
 * invented because the endpoint has no search param) plus a base-branch
 * Select fed by `GET /api/repositories/:repoId/branches`.
 *
 * Contributed by the coder feature; picked up by the P2 glob step registry
 * with ZERO edits to P2 files.
 *
 * Honesty (§9.2): the branch is a STARTING POINT — each coding job creates
 * its own `agent/<jobId>` work branch server-side (P5-T3); the helper copy
 * says so at the point of decision (D4). The selection is preserved in the
 * draft AND transmitted (P6-T3: create `overrides.coderConfig.repositoryId` /
 * `.branchName` → factory `coderConfig`, draft.ts
 * `collectCoderConfigOverride`; Q1/audit #2 closed).
 */
import { useEffect, useMemo } from 'react';

import { QueryErrorView } from '@/components/common/QueryErrorView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioCard } from '@/features/agents/components/RadioCard';
import type { AgentTemplateWizardStepType, WizardStepProps } from '@/features/agents/types';

import { useGetBranchesQuery, useGetRepositoriesQuery } from '../api/coderApi';
import { docId, gitUrlDisplay, providerLabel } from '../lib';
import type { AttachRepositoryValue, BranchView, RepositoryView } from '../types';
import { isAttachRepositoryValue } from '../types';

/** Defense in depth beyond the `currentData` gate: when a branch doc declares
 * its repository, it must be the selected one before it is emitted or reused. */
function branchMatchesRepository(branch: BranchView, repositoryId: string): boolean {
  return !branch.repositoryId || branch.repositoryId === repositoryId;
}

/** Draft slice builder (§9.1): "acme-api (GitHub) — base main". */
function toRepositoryValue(
  repository: Pick<RepositoryView, 'name' | 'provider'> & { id: string },
  branch?: BranchView,
): AttachRepositoryValue {
  const label = repository.provider
    ? `${repository.name} (${providerLabel(repository.provider)})`
    : repository.name;
  const gitBranchName = branch?.gitBranchName ?? '';
  return {
    repositoryId: repository.id,
    repositoryName: repository.name,
    provider: repository.provider,
    branchId: branch ? docId(branch) : '',
    gitBranchName,
    summary: gitBranchName ? `${label} — base ${gitBranchName}` : label,
  };
}

function AttachRepositoryStep({ step, value, onChange, errors, onErrors }: WizardStepProps) {
  const repositoriesQuery = useGetRepositoriesQuery({ page: 1, limit: 100 });
  const repositories = useMemo(() => repositoriesQuery.data ?? [], [repositoriesQuery.data]);
  const selected = isAttachRepositoryValue(value) ? value : undefined;

  const branchesQuery = useGetBranchesQuery(selected?.repositoryId ?? '', {
    skip: !selected?.repositoryId,
  });
  // Gate every derivation on `currentData` (RTK: data matching the CURRENT arg).
  // During the arg-change window of a repository switch `data` briefly still
  // holds the PREVIOUS repository's branches with `isLoading === false`; reading
  // it would lock a foreign branch id into the new repo's slice and the
  // `selected.branchId` guard would then block re-derivation (§7.2 E1).
  const branches = useMemo(() => branchesQuery.currentData ?? [], [branchesQuery.currentData]);

  const handleRepositorySelect = (repository: RepositoryView) => {
    // Switching repository resets the branch (it is re-derived, never
    // silently discarded — E1): the preselect effect below resolves it.
    onChange(toRepositoryValue({ ...repository, id: docId(repository) }));
  };

  const handleBranchSelect = (branchId: string) => {
    if (!selected) {
      return;
    }
    const branch = branches.find((entry) => docId(entry) === branchId);
    if (!branch || !branchMatchesRepository(branch, selected.repositoryId)) {
      return;
    }
    onChange(
      toRepositoryValue(
        {
          id: selected.repositoryId,
          name: selected.repositoryName,
          provider: selected.provider,
        },
        branch,
      ),
    );
  };

  // Preselect the default branch (G1): `isDefault`, else the first option.
  // `branches` is `currentData`-gated, so on a repository switch nothing is
  // emitted until the NEW repository's list lands — the branch is explicitly
  // re-derived (E1), never carried over from the previous repository.
  useEffect(() => {
    if (
      !selected ||
      selected.branchId ||
      branchesQuery.isLoading ||
      branchesQuery.isError ||
      branches.length === 0
    ) {
      return;
    }
    const candidates = branches.filter((branch) =>
      branchMatchesRepository(branch, selected.repositoryId),
    );
    const target = candidates.find((branch) => branch.isDefault) ?? candidates[0];
    if (target) {
      onChange(
        toRepositoryValue(
          {
            id: selected.repositoryId,
            name: selected.repositoryName,
            provider: selected.provider,
          },
          target,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolves whenever the slice loses its branch; onChange/branchesQuery identities churn per frame render.
  }, [selected, branches, branchesQuery.isLoading, branchesQuery.isError]);

  const reposSettled = !repositoriesQuery.isLoading && !repositoriesQuery.isError;
  const reposEmpty = reposSettled && repositories.length === 0;
  const missingRepo = step.required && !selected;
  const missingBranch = step.required && Boolean(selected) && !selected?.branchId;

  // Continue gating via the frame's step-errors contract (P4 precedent).
  useEffect(() => {
    if (step.required && reposEmpty) {
      onErrors({
        repository: 'A repository is required. Ask an administrator to connect one.',
      });
    } else if (missingRepo) {
      onErrors({ repository: 'Pick a repository to continue.' });
    } else if (missingBranch) {
      onErrors({ branch: 'Pick a branch to continue.' });
    } else {
      onErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onErrors identity changes per frame render; inputs are the stable derived flags.
  }, [step.required, reposEmpty, missingRepo, missingBranch]);

  if (repositoriesQuery.isError) {
    return (
      <QueryErrorView
        error={repositoriesQuery.error}
        label="repositories"
        onRetry={() => void repositoriesQuery.refetch()}
      />
    );
  }

  if (reposEmpty) {
    return (
      <EmptyState
        title="No repositories"
        description="Ask an administrator to connect a repository, then come back."
      />
    );
  }

  // "Settled" requires data matching the CURRENT arg: while a repository switch
  // is in flight (or its result hasn't landed), show the loading affordance
  // (C2) and re-derive the branch once the new list arrives (E1) — never flash
  // a false "No branches" from stale/absent data.
  const branchesLoading =
    branchesQuery.isLoading || (!branchesQuery.isError && branchesQuery.currentData === undefined);
  const branchesEmpty = !branchesLoading && branches.length === 0;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">The repository your coding agent works in.</p>

      <div className="space-y-2">
        <p className="text-sm font-medium" id={`attach-repo-label-${step.id}`}>
          Repository
        </p>
        {repositoriesQuery.isLoading ? (
          <LoadingRows label="Loading repositories…" columns={2} rows={3} />
        ) : (
          <>
            <div
              className="space-y-2"
              role="radiogroup"
              aria-labelledby={`attach-repo-label-${step.id}`}
            >
              {repositories.map((repository) => {
                const id = docId(repository);
                return (
                  <RadioCard
                    key={id}
                    groupName={`attach-repository-${step.id}`}
                    value={id}
                    checked={Boolean(selected && selected.repositoryId === id)}
                    onSelect={() => handleRepositorySelect(repository)}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-medium">{repository.name}</span>
                      {repository.provider ? (
                        // Text labels only — no invented provider icons (C5).
                        <Badge variant="outline" className="rounded-sm">
                          {providerLabel(repository.provider)}
                        </Badge>
                      ) : null}
                    </span>
                    {repository.gitUrl ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="mt-1 block max-w-[20rem] truncate font-mono text-xs text-muted-foreground">
                            {gitUrlDisplay(repository.gitUrl)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs break-all">
                          {repository.gitUrl}
                        </TooltipContent>
                      </Tooltip>
                    ) : null}
                  </RadioCard>
                );
              })}
            </div>
            {repositories.length >= 100 ? (
              <p className="text-sm text-muted-foreground">Showing the first 100 repositories.</p>
            ) : null}
          </>
        )}
        {errors.repository ? (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
          >
            {errors.repository}
          </p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`attach-repo-branch-${step.id}`}>Base branch</Label>
        {!selected ? (
          <p className="text-sm text-muted-foreground">Pick a repository first.</p>
        ) : branchesQuery.isError ? (
          <QueryErrorView
            error={branchesQuery.error}
            label="branches"
            onRetry={() => void branchesQuery.refetch()}
          />
        ) : branchesEmpty ? (
          <EmptyState title="No branches" description="This repository has no branches yet." />
        ) : (
          <Select
            value={selected.branchId || undefined}
            onValueChange={handleBranchSelect}
            disabled={branchesLoading}
          >
            <SelectTrigger id={`attach-repo-branch-${step.id}`} className="w-full max-w-sm">
              <SelectValue
                placeholder={branchesLoading ? 'Loading branches…' : 'Select a branch'}
              />
            </SelectTrigger>
            <SelectContent>
              {branches.map((branch) => {
                const id = docId(branch);
                return (
                  <SelectItem key={id} value={id}>
                    {branch.gitBranchName}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        <p className="text-sm text-muted-foreground">
          The branch the agent starts from. Each job creates its own work branch from it.
        </p>
        {errors.branch ? (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
          >
            {errors.branch}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'attach-repository';
export const component = AttachRepositoryStep;
